import { useState, useMemo } from 'react';
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import type { EmailTemplateVariable } from '@/types/emailTemplate';
import { renderTemplate, buildExampleVariables } from '@/services/emailTemplateApi';

interface TemplatePreviewProps {
  subject: string;
  htmlContent: string;
  variables: EmailTemplateVariable[];
  fromName: string;
  fromEmail: string;
  onSendTest?: (testEmail: string) => Promise<void>;
  isSendingTest?: boolean;
  className?: string;
}

export default function TemplatePreview({
  subject,
  htmlContent,
  variables,
  fromName,
  fromEmail,
  onSendTest,
  isSendingTest = false,
  className,
}: TemplatePreviewProps) {
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Build example variables for preview
  const exampleVariables = useMemo(() => buildExampleVariables(variables), [variables]);

  // Render template with example values
  const renderedSubject = useMemo(
    () => renderTemplate(subject, exampleVariables),
    [subject, exampleVariables]
  );

  const renderedContent = useMemo(
    () => renderTemplate(htmlContent, exampleVariables),
    [htmlContent, exampleVariables]
  );

  const handleSendTest = async () => {
    if (onSendTest && testEmail) {
      try {
        await onSendTest(testEmail);
        setShowTestModal(false);
        setTestEmail('');
      } catch (error) {
        // Error is handled by the mutation's onError callback
        // Just close the modal
        setShowTestModal(false);
        setTestEmail('');
      }
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Preview Header */}
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Preview</span>

          {/* Device Toggle */}
          <div className="flex items-center bg-gray-200 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setPreviewDevice('desktop')}
              className={`p-1.5 rounded ${
                previewDevice === 'desktop'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Desktop Preview"
            >
              <ComputerDesktopIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setPreviewDevice('mobile')}
              className={`p-1.5 rounded ${
                previewDevice === 'mobile'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Mobile Preview"
            >
              <DevicePhoneMobileIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {onSendTest && (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
            onClick={() => setShowTestModal(true)}
          >
            Send Test
          </Button>
        )}
      </div>

      {/* Email Header Preview */}
      <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
        <div className="space-y-1.5 text-sm">
          <div className="flex">
            <span className="text-gray-500 w-16">From:</span>
            <span className="text-gray-900">
              {fromName} &lt;{fromEmail}&gt;
            </span>
          </div>
          <div className="flex">
            <span className="text-gray-500 w-16">Subject:</span>
            <span className="text-gray-900 font-medium">{renderedSubject}</span>
          </div>
        </div>
      </div>

      {/* Email Body Preview */}
      <div
        className={`bg-white overflow-auto transition-all ${
          previewDevice === 'mobile' ? 'max-w-[375px] mx-auto border-x border-gray-200' : ''
        }`}
        style={{ maxHeight: '500px' }}
      >
        <iframe
          srcDoc={`
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                  body {
                    margin: 0;
                    padding: 16px;
                    font-family: Arial, sans-serif;
                    font-size: 14px;
                    line-height: 1.5;
                    color: #333;
                    background: #fff;
                  }
                  img {
                    max-width: 100%;
                    height: auto;
                  }
                  a {
                    color: #3B82F6;
                  }
                </style>
              </head>
              <body>
                ${renderedContent}
              </body>
            </html>
          `}
          title="Email Preview"
          className="w-full border-0"
          style={{
            height: '400px',
            minHeight: '400px',
          }}
          sandbox="allow-same-origin"
        />
      </div>

      {/* Variable Legend */}
      {variables.length > 0 && (
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
          <p className="text-xs text-gray-500 mb-2">
            Preview shows example values. Actual values will be populated when emails are sent.
          </p>
          <div className="flex flex-wrap gap-2">
            {variables.slice(0, 5).map((v) => (
              <span
                key={v.id}
                className="inline-flex items-center gap-1 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded"
              >
                <code>{v.variable_key}</code>
                <span className="text-gray-400">=</span>
                <span>{v.example_value || v.default_value || '...'}</span>
              </span>
            ))}
            {variables.length > 5 && (
              <span className="text-xs text-gray-400">+{variables.length - 5} more</span>
            )}
          </div>
        </div>
      )}

      {/* Send Test Modal */}
      <Modal
        isOpen={showTestModal}
        onClose={() => {
          setShowTestModal(false);
          setTestEmail('');
        }}
        title="Send Test Email"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Send a test email to verify the template looks correct. Example values will be used for
            all variables.
          </p>
          <div>
            <label htmlFor="template-test-email" className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Email *
            </label>
            <input
              id="template-test-email"
              name="testEmail"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="your@email.com"
              autoComplete="email"
              aria-label="Recipient email"
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <p className="font-medium text-gray-700">Email will include:</p>
            <ul className="mt-1 text-gray-600 space-y-1">
              <li>
                <span className="text-gray-400">Subject:</span> {renderedSubject}
              </li>
              <li>
                <span className="text-gray-400">From:</span> {fromName} &lt;{fromEmail}&gt;
              </li>
            </ul>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendTest}
            loading={isSendingTest}
            disabled={!testEmail || !testEmail.includes('@')}
          >
            Send Test Email
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
